// This hook is used to fetch and manage memory data from the GlobalStore
// It is used in the MemoryModal.tsx and AddNewMemory.tsx.
// [
//   {
//     "id": "uuidv4",
//     "text": "Umut is 21 years old"
//   }
// ]
import { useGlobalStore } from '@highlight/ipc/react'
import { AboutMe } from '@shared/types/AboutMe'
import { useCallback, useMemo } from 'react'

/**
 * Store keys for facts k/v store.
 */
export const FactsPreferenceKeys = {
  facts: 'facts',
  aboutMe: 'aboutMe',
}

const useFacts = () => {
  const [facts, setFactsItems, clearFactsItems] = useGlobalStore<string[]>('facts', FactsPreferenceKeys.facts, [])
  const [aboutMe, setAboutMe] = useGlobalStore<AboutMe>('facts', FactsPreferenceKeys.aboutMe, {})

  const aboutMeString = useMemo(() => {
    return aboutMe
      ? Object.entries(aboutMe)
          .filter(([_, value]) => value)
          .map(
            ([key, value]) =>
              `${key}: ${
                key === 'companyMetadata'
                  ? Object.entries(value as Record<string, unknown>)
                      .map(([k, v]) => `Company website ${k}: ${v}`)
                      .join('\n')
                  : value
              }`,
          )
          .join('\n')
      : ''
  }, [aboutMe])

  const addFact = useCallback(
    (text: string) => {
      if (facts.includes(text)) {
        return
      }
      setFactsItems([...facts, text])
    },
    [setFactsItems, facts],
  )

  const addFacts = useCallback(
    (...newFacts: string[]) => {
      setFactsItems((prev) => [...(prev ?? []), ...newFacts])
    },
    [setFactsItems],
  )

  const removeFact = useCallback(
    (text: string) => {
      if (!facts.includes(text)) {
        return
      }
      setFactsItems(facts.filter((id) => id !== text))
    },
    [facts, setFactsItems],
  )

  const updateFact = useCallback(
    (factIndex: number, text: string) => {
      setFactsItems((prev) => {
        const newFacts = prev ? [...prev] : []
        newFacts[factIndex] = text
        return newFacts
      })
    },
    [setFactsItems],
  )

  return useMemo(() => {
    return {
      facts,
      setFactsItems,
      clearFactsItems,
      addFact,
      addFacts,
      removeFact,
      updateFact,
      aboutMe,
      setAboutMe,
      aboutMeString,
    }
  }, [
    facts,
    setFactsItems,
    clearFactsItems,
    addFact,
    addFacts,
    removeFact,
    updateFact,
    aboutMe,
    setAboutMe,
    aboutMeString,
  ])
}

export { useFacts }
